/**
 * Masterclass GA4 ecommerce tracking helper.
 *
 * Single source of truth for all gtag events. Every event includes proper
 * GA4 ecommerce schema with item-level metadata so Smart Bidding can learn
 * which plate categories actually convert.
 *
 * Plate classification is done client-side via regex — no extra HTTP calls.
 */

export type PlateClassification = {
  /** "DVLA" | "Cherished" | "Dealer" — used as item_brand */
  brand: string;
  /** "Suffix" | "Prefix" | "Current" | "Dateless" | "Dateless-reversed" | "Northern-Irish" | "Other" */
  category: string;
  /** Era/period e.g. "1963-1982" — used as item_variant */
  variant: string;
};

/**
 * Classify a UK plate from its registration string. No I/O — pure regex.
 */
export const classifyPlate = (registration: string, price?: number): PlateClassification => {
  const r = (registration || "").replace(/\s/g, "").toUpperCase();

  // DVLA reserves prices in standard bands; below that price tier likely bought-in DVLA
  // Without server data we default brand to DVLA for new-format plates, Cherished for older
  let category = "Other";
  let variant = "";

  if (/^[A-Z]{2}[0-9]{2}[A-Z]{3}$/.test(r)) {
    category = "Current";
    variant = "2001-present";
  } else if (/^[A-Z][0-9]{1,3}[A-Z]{3}$/.test(r)) {
    category = "Prefix";
    variant = "1983-2001";
  } else if (/^[A-Z]{3}[0-9]{1,3}[A-Z]$/.test(r)) {
    category = "Suffix";
    variant = "1963-1982";
  } else if (/^(IB|IL|JI|UI|MI|GZ|JZ|KZ|MZ|OZ|PZ|RZ|SZ|TZ|UZ|VZ|WZ|XZ|YZ)/.test(r)) {
    category = "Northern-Irish";
    variant = "Dateless-NI";
  } else if (/^[A-Z]{1,3}[0-9]{1,4}$/.test(r)) {
    category = "Dateless";
    variant = "pre-1963";
  } else if (/^[0-9]{1,4}[A-Z]{1,3}$/.test(r)) {
    category = "Dateless-reversed";
    variant = "pre-1963";
  }

  // Brand heuristic: Current/Prefix/Suffix at sub-£5k typical = DVLA stock,
  // anything else (or high-value suffix/prefix) is Cherished/dealer
  let brand = "DVLA";
  if (category === "Dateless" || category === "Dateless-reversed" || category === "Northern-Irish") {
    brand = "Cherished";
  } else if (price && price > 5000) {
    brand = "Cherished";
  }

  return { brand, category, variant };
};

/**
 * Build a GA4 items[] entry with full masterclass metadata.
 */
export const buildItem = (
  registration: string,
  price?: number,
  itemListName?: string
) => {
  const cls = classifyPlate(registration, price);
  return {
    item_id: registration,
    item_name: registration,
    item_brand: cls.brand,
    item_category: cls.category,
    item_variant: cls.variant,
    price: price || 0,
    quantity: 1,
    ...(itemListName ? { item_list_name: itemListName } : {}),
  };
};

const isGtag = (): boolean => typeof (window as any).gtag === "function";

/**
 * Fire an event to every loaded platform.
 * Priority order:
 *   1. window.nrTrack — fans out to nr-track + GA4 + Bing + Meta + TikTok (defined in
 *      html.foot.php, wrapped by t.js). Preferred path when available.
 *   2. Fallback: send directly to nr-track via sendBeacon (/collect proxied through nginx)
 *      AND fire gtag directly — covers cases where nrTrack hasn't loaded yet.
 * This means nr-track always gets the event regardless of GA4 state.
 */
const emit = (eventName: string, params: Record<string, any>): void => {
  const w = window as any;
  if (typeof w.nrTrack === "function") {
    // t.js has wrapped nrTrack — single call fans out to nr-track + all ad platforms
    w.nrTrack(eventName, params);
  } else {
    // nrTrack not ready yet — send directly to nr-track and gtag separately
    try {
      const payload = JSON.stringify({
        site_id: "NEWREG",
        event_type: eventName,
        url: window.location.href,
        ...params,
      });
      if (navigator.sendBeacon) {
        navigator.sendBeacon("/collect", new Blob([payload], { type: "application/json" }));
      }
    } catch (e) {}
    if (typeof w.gtag === "function") {
      w.gtag("event", eventName, params);
    }
  }
};

/**
 * Push user_id and user_properties to GA4 — call on login, on app mount if
 * already authenticated, and after sign-up. Survives across pages.
 */
export const setUser = (userCustId?: string | null, properties?: Record<string, any>) => {
  if (!isGtag()) return;
  const cfg: Record<string, any> = {};
  if (userCustId) cfg.user_id = userCustId;
  (window as any).gtag("config", "G-80ZZ44BFFB", cfg);
  if (properties && Object.keys(properties).length > 0) {
    (window as any).gtag("set", "user_properties", properties);
  }
};

// -----------------------------------------------------------------------------
// Standard GA4 ecommerce events
// -----------------------------------------------------------------------------

export const trackSearch = (searchTerm: string) => {
  emit("search", { search_term: searchTerm });
};

export const trackViewItemList = (
  registrations: Array<{ registration: string; price?: number }>,
  itemListName: string
) => {
  if (!registrations || registrations.length === 0) return;
  emit("view_item_list", {
    item_list_name: itemListName,
    items: registrations.map((r) => buildItem(r.registration, r.price, itemListName)),
  });
};

export const trackSelectItem = (
  registration: string,
  price?: number,
  itemListName?: string
) => {
  emit("select_item", {
    item_list_name: itemListName,
    items: [buildItem(registration, price, itemListName)],
  });
};

export const trackViewItem = (registration: string, price?: number) => {
  emit("view_item", {
    currency: "GBP",
    value: price || 0,
    items: [buildItem(registration, price)],
  });
};

export const trackAddToCart = (registration: string, price?: number) => {
  emit("add_to_cart", {
    currency: "GBP",
    value: price || 0,
    items: [buildItem(registration, price)],
  });
};

export const trackBeginCheckout = (registration: string, price?: number, email?: string) => {
  emit("begin_checkout", {
    currency: "GBP",
    value: price || 0,
    ...(email ? { user_id: email } : {}),
    items: [buildItem(registration, price)],
  });
};

export const trackAddPaymentInfo = (
  registration: string,
  price?: number,
  paymentType?: string
) => {
  emit("add_payment_info", {
    currency: "GBP",
    value: price || 0,
    payment_type: paymentType || "credit_card",
    items: [buildItem(registration, price)],
  });
};

export const trackPurchase = (
  registration: string,
  totalValue: number,
  paymentType?: string,
  transactionId?: string,
  email?: string
) => {
  emit("purchase", {
    transaction_id: transactionId || `${registration}_${Date.now()}`,
    value: totalValue,
    currency: "GBP",
    payment_type: paymentType || "credit_card",
    ...(email ? { user_id: email } : {}),
    items: [buildItem(registration, totalValue)],
  });
};

export const trackGenerateLead = (
  type: "enquire" | "offer" | "valuation" | "sell",
  registration?: string,
  value?: number
) => {
  if (!isGtag()) return;
  emit("generate_lead", {
    currency: "GBP",
    value: value || 0,
    lead_type: type,
    ...(registration ? { items: [buildItem(registration, value)] } : {}),
  });
};

export const trackSignUp = (method: string = "email") => {
  if (!isGtag()) return;
  emit("sign_up", { method });
};

export const trackLogin = (method: string = "email") => {
  if (!isGtag()) return;
  emit("login", { method });
};

export const trackPaymentMethodSelect = (paymentType: string) => {
  emit("payment_method_select", { payment_type: paymentType.toLowerCase() });
};

export const trackAddToWishlist = (registration: string, price?: number) => {
  emit("add_to_wishlist", {
    currency: "GBP",
    value: price || 0,
    items: [buildItem(registration, price)],
  });
};

export const trackFinanceSelect = (registration: string, optionKey: string, depositPercent: number, monthlyAmount?: number) => {
  emit("finance_option_select", {
    registration,
    option_key: optionKey,
    deposit_percent: depositPercent,
    monthly_amount: monthlyAmount || 0,
  });
};

export const trackEmailCapture = (email: string, context: string) => {
  emit("email_capture", { email, context });
};

export const trackDiscountCode = (action: "apply" | "remove" | "fail", code: string, registration?: string) => {
  emit("discount_code", { action, code, registration });
};

export const trackPaymentDeclined = (
  registration: string,
  price?: number,
  paymentType?: string,
  reason?: string
) => {
  emit("payment_declined", {
    currency: "GBP",
    value: price || 0,
    payment_type: paymentType || "credit_card",
    ...(reason ? { reason } : {}),
    items: [buildItem(registration, price)],
  });
};

export const trackCheckoutValidationBlocked = (
  registration: string,
  errorFields: string,
  step: string = "customise"
) => {
  emit("checkout_validation_blocked", {
    registration,
    error_fields: errorFields,
    step,
  });
};
